import { useCallback, useMemo } from "react";
import { NetworkStatus } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { scopeIsWorkflowBound } from "#app/lib/filters/issue-scope";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { combineWhereConditions } from "#app/lib/query-utils";
import { useScopedActivityWhere } from "#app/widgets/dashboards/scope/use-scoped-activity-where";
import { ActivityFeedDocument, type ActivityWhereInput } from "#app/lib/data";
import type { ActivityNode } from "#app/components/issue-view/activity/activity-types";
import type { Omittable } from "@salestrak/ui";
import { DEFAULT_PAGE_SIZE } from "#app/lib/constants/page-size";

/**
 * Feed row — the shared issue-view `ActivityNode` plus the parent issue. A
 * single-issue timeline has its issue implicitly; a cross-issue feed must
 * attribute each row and navigate to it on press.
 */
export type ActivityFeedRow = ActivityNode & Omittable<{
  issue?: { id: string; issueId: string; name: string } | null;
}>;

/**
 * Real user actions + notes — the same signal the issue timeline shows, minus the
 * pure-system churn (age expiries with no actor). Mirrors the `ActivityViewData`
 * base predicate so the feed reads identically to a project's own timeline.
 */
const ACTIVITY_BASE: ActivityWhereInput = {
  OR: [{ createdBy: { exists: true } }, { subject: { eq: "note" } }],
};

/**
 * Which feed the card lists. `"actions"`: the scoped feed as the dashboard
 * frames it — on My Work, the actions the member took. `"items"`: what
 * happened ON the projects assigned to the viewer, by anyone but them —
 * stage moves, comments, edits — so the card reads as "changes on my work"
 * rather than "what I did".
 */
export type ActivityFeed = "actions" | "items";

/** Newest first — module-level so the query variables stay referentially stable. */
const ORDER_BY = { creationDate: "desc" } as const;
const PAGE_SIZE = DEFAULT_PAGE_SIZE;

/**
 * The data layer for the `activity-list` (Activity feed) card: the scoped activity
 * rows it lists, **lazily paged** — the first page loads eagerly and `loadMore`
 * appends the next `skip`-offset page as the viewer scrolls, until the server
 * `total` is exhausted. The whole scoped feed is reachable; nothing is a capped
 * chunk. Folds the dashboard window through the activity-shaped twin of the
 * issue fold (`useScopedActivityWhere`) — a **team**-scoped dashboard narrows there
 * by the activity's teams, a **member**-scoped one (My Work) by its actor; a
 * **workflow**-scoped one folds its pinned entity through `issueWhere` instead
 * (activities carry no workflow column — the server resolves the parent issues),
 * the same compensation the sibling count widgets use, so the feed can never
 * disagree with the charts on its own board. Reads the shared
 * `ActivityFeed` query and maps edges to the same `ActivityNode` shape `BaseItem`
 * renders. A card never hand-builds this `where`.
 */
export function useActivityList(feed: ActivityFeed = "actions"): {
  rows: ActivityFeedRow[];
  loading: boolean;
  /** A further page is in flight — show a tail skeleton, don't re-fire. */
  loadingMore: boolean;
  /** More rows exist past the loaded window. */
  hasMore: boolean;
  /** Append the next page. No-op while loading or exhausted. */
  loadMore: () => void;
} {
  const { userId } = useWorkspaceData();
  const onItems = feed === "items";
  // On the viewer's items, their own actions are left out: the card says what
  // OTHERS did to what they hold. Inline is fine — the fold serializes `base`.
  const base: ActivityWhereInput =
    onItems && userId ? { AND: [ACTIVITY_BASE, { createdBy: { ne: userId } }] } : ACTIVITY_BASE;
  const { where, scope } = useScopedActivityWhere(base, {
    memberNarrowing: onItems ? "items" : "actor",
  });
  // The issue-side compensation: a workflow scope always folds its pinned
  // entity here (activities carry no workflow column), and the items feed
  // adds the viewer's assignee predicate — the same predicate the member
  // scope pins — so the feed is the activity on THEIR projects in scope.
  const workflowWhere =
    scope.entity && scopeIsWorkflowBound(scope.entity.type) ? scope.entityWhere : undefined;
  const viewerId = onItems ? userId : undefined;
  const issueWhere = useMemo(() => {
    const parts: Record<string, unknown>[] = [];
    if (workflowWhere) parts.push(workflowWhere);
    if (viewerId) parts.push({ assignees: { entityId: { hasSome: [viewerId] } } });
    return parts.length === 0 ? undefined : combineWhereConditions(parts);
  }, [workflowWhere, viewerId]);

  const variables = useMemo(
    () => ({ where, limit: PAGE_SIZE, orderBy: ORDER_BY, issueWhere }),
    [where, issueWhere],
  );
  const { data, loading, fetchMore, networkStatus } = useQuery(ActivityFeedDocument, {
    variables,
    notifyOnNetworkStatusChange: true,
  });
  const rows = useMemo(
    () => (data?.activities?.edges ?? []).map((e) => e.node) as ActivityFeedRow[],
    [data],
  );

  const total = data?.activities?.total ?? 0;
  const loadingMore = networkStatus === NetworkStatus.fetchMore;
  const hasMore = rows.length < total;

  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    void fetchMore({
      variables: { skip: rows.length },
      // Append the page onto the loaded window. The activities cache is
      // per-page (no field merge fn — the windowed tables need replacement
      // semantics), so the accumulation lives in this query's local view.
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.activities) return prev;
        if (!prev?.activities) return fetchMoreResult;
        return {
          ...prev,
          activities: {
            ...fetchMoreResult.activities,
            edges: [...prev.activities.edges, ...fetchMoreResult.activities.edges],
          },
        };
      },
    });
  }, [fetchMore, loading, loadingMore, hasMore, rows.length]);

  return { rows, loading: loading && !data, loadingMore, hasMore, loadMore };
}
